import React, { Component, createRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { BRAND, getUID, sendNotfication, API } from "../../../Helper";
import PaymentBar from "../../Components/PaymentBar";
import MobilePaymentBR from "../../Components/MobilePaymetBar";
import CasinoLeaderboard from "../../Components/HelloGamesTable";
import GameProviders from "../Parts/Home/Provider";
import CASINO_GAMES_CONFIG from "../../../config/casinoGamesConfig";

const HUIDU_BASE = `${API}/huiduapi`;
const CACHE_PFX = "casino_";
const CACHE_TTL = 3_600_000;

function cacheGet(k) {
  try {
    const r = sessionStorage.getItem(CACHE_PFX + k);
    if (!r) return null;
    const { ts, data } = JSON.parse(r);
    if (Date.now() - ts > CACHE_TTL) { sessionStorage.removeItem(CACHE_PFX + k); return null; }
    return data;
  } catch { return null; }
}
function cacheSet(k, d) {
  try { sessionStorage.setItem(CACHE_PFX + k, JSON.stringify({ ts: Date.now(), data: d })); } catch {}
}

function mapGame(item, idx) {
  const uid = item.game_uid || item.uid || item.id || `hd-${idx}`;
  return {
    id: uid.toString(),
    game_uid: uid.toString(),
    title: item.game_name || item.name || item.title || `Game ${idx + 1}`,
    game_icon: item.icon_url || item.image_url || item.cover || item.thumbnail || item.image || null,
    supplier: item.supplier_code || item.supplier || null,
  };
}

// ── Styled components ────────────────────────────────────────
const FullScreenContainer = styled.div`position:fixed;inset:0;z-index:100001;background:#292d2e;`;
const FullScreenIframe = styled.iframe`width:100%;height:100%;border:none;`;
const BackButton = styled.button`
  position:absolute;top:10px;left:10px;z-index:1000;
  background:none;border:none;color:#fff;font-size:24px;cursor:pointer;
`;
const GameOverlay = styled.div`
  position:absolute;inset:0;background:rgba(0,0,0,.45);border-radius:8px;
  display:flex;justify-content:center;align-items:center;
  opacity:0;transition:opacity .2s;
`;
const GameCard = styled.div`
  width: 142px;
  min-width: 142px;
  height: 190px;
  border-radius: 10px;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  flex-shrink: 0;
  background: #1e2223;
  transition: transform .2s;
  &:hover { transform: translateY(-4px); }
  &:hover ${GameOverlay} { opacity: 1; }
  @media(max-width: 768px) { width: 115px; min-width: 115px; height: 155px; }
`;
const GameImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 10px;
  display: block;
`;
const PlayCircle = styled.div`
  width:46px;height:46px;border-radius:50%;background:rgba(255,255,255,.28);
  display:flex;align-items:center;justify-content:center;
`;
const ScrollRow = styled.div`
  display:flex;overflow-x:auto;gap:10px;scrollbar-width:none;
  &::-webkit-scrollbar{display:none;}padding-bottom:6px;
`;
const GridWrap = styled.div`
  display:grid;grid-template-columns:repeat(8,1fr);gap:10px;
  @media(max-width:900px){grid-template-columns:repeat(5,1fr);}
  @media(max-width:600px){grid-template-columns:repeat(3,1fr);}
`;
const RowHeader = styled.div`
  display:flex;justify-content:space-between;align-items:center;margin:16px 0 8px;
`;
const RowTitle = styled.div`color:#fff;font-size:16px;font-weight:700;`;
const IconBtn = styled.div`
  padding:6px 9px;background:#2e3436;border-radius:10px;
  cursor:pointer;display:inline-flex;align-items:center;gap:4px;
  color:#fff;font-size:13px;font-weight:600;
  &:hover{background:#4a5354;}
`;
const Skel = styled.div`
  width:100%;height:100%;border-radius:8px;
  background:linear-gradient(90deg,
    rgba(190,190,190,.15) 25%,rgba(129,129,129,.22) 37%,rgba(190,190,190,.15) 63%);
  background-size:400% 100%;
  animation:sk 1.4s ease infinite;
  @keyframes sk{0%{background-position:100% 50%}100%{background-position:0 50%}}
`;
const PagRow = styled.div`display:flex;justify-content:center;align-items:center;gap:12px;margin-top:12px;`;
const PagBtn = styled.button`
  padding:7px 14px;border:none;border-radius:6px;
  background:${p => p.disabled ? "#3a3a3a" : "#4a4a4a"};
  color:${p => p.disabled ? "#666" : "#fff"};
  cursor:${p => p.disabled ? "not-allowed" : "pointer"};
  display:flex;align-items:center;gap:4px;font-size:13px;
`;

// ── ProviderRow ──────────────────────────────────────────────
// Shows curated games instantly. Show More fetches live from API.
class ProviderRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      liveGames: [],
      liveLoading: false,
      livePage: 1,
      liveTotalPages: 1,
    };
    this.rowRef = createRef();
    this._mounted = false;
    this._scrollEl = null;
  }

  componentDidMount() { this._mounted = true; }
  componentWillUnmount() { this._mounted = false; }

  handleShowMore = () => {
    this.setState(prev => ({ expanded: !prev.expanded }));
  };

  scrollRow = (dir) => {
    if (this._scrollEl) this._scrollEl.scrollBy({ left: dir * 420, behavior: "smooth" });
  };

  render() {
    const { provider, onLaunch } = this.props;
    const { expanded } = this.state;
    const curatedGames = provider.games || [];

    const renderCard = (game, idx) => {
      const key = game.game_uid || game.id || `${provider.code}-${idx}`;
      const src = game.image || game.game_icon || "/assets/images/default_card3.png";
      const name = game.game_name || game.title || "";
      return (
        <GameCard
          key={key}
          onClick={() => onLaunch({ ...game, id: game.game_uid || game.id, supplier: game.supplier || provider.code })}
        >
          <GameImage
            src={src}
            alt={name}
            loading="lazy"
            onError={e => { e.target.src = "/assets/images/default_card3.png"; }}
          />
          <GameOverlay>
            <PlayCircle><Play color="#fff" size={20} /></PlayCircle>
          </GameOverlay>
        </GameCard>
      );
    };

    const skelCards = (n) => Array.from({ length: n }).map((_, i) => (
      <div key={`sk-${i}`} style={{ minWidth: 142, height: 195, flexShrink: 0 }}><Skel /></div>
    ));

    return (
      <div ref={this.rowRef} style={{ marginTop: 4 }}>
        <RowHeader>
          <RowTitle>{provider.name}</RowTitle>
          <div style={{ display: "flex", gap: 8 }}>
            <IconBtn onClick={this.handleShowMore}>
              {expanded ? "Show Less" : "Show More"}
            </IconBtn>
            {!expanded && (
              <>
                <IconBtn onClick={() => this.scrollRow(-1)}><ChevronLeft size={15} /></IconBtn>
                <IconBtn onClick={() => this.scrollRow(1)}><ChevronRight size={15} /></IconBtn>
              </>
            )}
          </div>
        </RowHeader>

        {!expanded ? (
          <ScrollRow ref={r => (this._scrollEl = r)}>
            {curatedGames.length === 0 ? skelCards(8) : curatedGames.map(renderCard)}
          </ScrollRow>
        ) : (
          <GridWrap>
            {curatedGames.map(renderCard)}
          </GridWrap>
        )}
      </div>
    );
  }
}

// ── Main Casino page ─────────────────────────────────────────
export default class Casino extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileView: window.innerWidth <= 768,
      iframeShow: false,
      iframeUrl: "",
      iframeLoading: true,
      launching: false,
    };
  }

  componentDidMount() { window.addEventListener("resize", this.onResize); }
  componentWillUnmount() { window.removeEventListener("resize", this.onResize); }
  onResize = () => this.setState({ isMobileView: window.innerWidth <= 768 });

  handleLaunch = async (game) => {
    const uid = getUID();
    if (!uid) { sendNotfication("Please login to play", "error"); return; }
    this.setState({ launching: true });
    try {
      const currency = localStorage.getItem("coin") || "PKR";
      const gameUid = game.game_uid || game.id || "";
      const res = await fetch(`${HUIDU_BASE}/game/play`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ game_uid: gameUid, user_id: uid, currency_code: currency, supplier_code: game.supplier || '', language: "en" }),
      });
      const json = await res.json();
      if (json.success && json.data?.game_launch_url) {
        this.setState({ iframeUrl: json.data.game_launch_url, iframeShow: true, iframeLoading: true });
      } else {
        sendNotfication(json.error || json.message || "Failed to launch game", "error");
      }
    } catch (err) {
      sendNotfication(err?.message || "Unexpected error", "error");
    } finally {
      this.setState({ launching: false });
    }
  };

  render() {
    const { isMobileView, iframeShow, iframeUrl, iframeLoading, launching } = this.state;
    const providers = [...CASINO_GAMES_CONFIG].sort((a, b) => (a.order || 99) - (b.order || 99));

    if (iframeShow) {
      return (
        <FullScreenContainer>
          {iframeLoading && (
            <img src="assets/images/lg.png" alt="Loading"
              style={{ position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%,-50%)", width: 220, animation: "fadeinout 2s infinite" }} />
          )}
          <FullScreenIframe src={iframeUrl} onLoad={() => this.setState({ iframeLoading: false })} />
          <BackButton onClick={() => this.setState({ iframeShow: false, iframeUrl: "", iframeLoading: true })}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackButton>
        </FullScreenContainer>
      );
    }

    return (
      <>
        <Helmet><title>{BRAND} - Crypto Casino Games</title></Helmet>

        {launching && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
            zIndex: 998, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ color: "#fff", fontSize: 15 }}>Launching game…</div>
          </div>
        )}

        <div style={{ marginTop: -10 }}>
          {providers.map((p, i) => (
            <React.Fragment key={p.code}>
              <ProviderRow provider={p} onLaunch={this.handleLaunch} />
              {i === 4 && (
                <div style={{ margin: "20px 0" }}><GameProviders /></div>
              )}
            </React.Fragment>
          ))}
          <div style={{ marginTop: 24 }}>
            {!isMobileView ? <PaymentBar /> : <MobilePaymentBR />}
          </div>
        </div>

        <CasinoLeaderboard />
      </>
    );
  }
}
